import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconChevron } from '../../icon/IconChevron';
import { Button } from '../../input/Button';
import { Box, BoxProps } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import {
    MULTIPLE_THUMB_HEIGHT,
    MULTIPLE_THUMB_WIDTH,
    THUMB_HEIGHT,
    THUMB_WIDTH,
} from './constants';
import { OverlayLinkProps } from './types';

export const OverlayLink = styled(Button)
    .withConfig({ displayName: 'OverlayLink' })
    .attrs<OverlayLinkProps>((props) => ({
        ...props,
        as: Link,
        padding: '0px',
        height: 'fit-content',
        width: '100%',
        borderRadius: '24px',
        target: '_blank',
        variant: 'filled',
        color: 'secondary',
        textTransform: 'none',
        textAlign: 'left',
        isHoverEnabled: false,
    }))<OverlayLinkProps>``;

export const ImageWrapper = styled(Box)
    .withConfig({ displayName: 'ImageWrapper' })
    .attrs<{ isMultipleProducts?: boolean } & BoxProps>(({ isMultipleProducts, opacity }) => ({
        height: isMultipleProducts ? MULTIPLE_THUMB_HEIGHT : THUMB_HEIGHT,
        width: isMultipleProducts ? MULTIPLE_THUMB_WIDTH : THUMB_WIDTH,
        minWidth: isMultipleProducts ? MULTIPLE_THUMB_WIDTH : THUMB_WIDTH,
        borderRadius: isMultipleProducts ? '20px' : '16px',
        backgroundColor: 'var(--always-white)',
        overflow: 'hidden',
        opacity,
    }))`
    align-content: center;
`;

export const Content = styled(Flex)
    .withConfig({ displayName: 'Content' })
    .attrs<{ isMultipleProducts?: boolean }>(({ isMultipleProducts }) => ({
        direction: 'column',
        width: '100%',
        margin: `auto 0 auto ${isMultipleProducts ? '12px' : '16px'}`,
        p: 0.5,
    }))`
    transition: opacity 0.2s ease-in-out;
    @media (hover: hover) and (pointer: fine) {
        ${OverlayLink}:hover & {
            opacity: 0.6;
        }
    }
    `;

export const Chevron = styled(IconChevron).withConfig({ displayName: 'Chevron' }).attrs({
    size: 24,
    color: 'var(--color-primary)',
})`
    transform: rotate(-90deg);
    margin: auto 4px auto 0;
`;

export const ChevronMultiple = styled(Button).withConfig({
    displayName: 'ChevronMultiple',
})`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin: 8px 4px 8px 16px;
    flex-shrink: 0;
`;

export const RelativeContainer = styled(Flex).withConfig({
    displayName: 'RelativeContainer',
})`
    position: relative;
    flex-direction: row;
    align-items: center;
    width: 40px;
    min-width: 40px;
    height: ${MULTIPLE_THUMB_HEIGHT};
    margin: 0 4px 0 16px;
`;

export const ExpandableButtonWrapper = styled(Box).withConfig({
    displayName: 'ExpandableButtonWrapper',
})`
    position: absolute;
    right: 0;
    border-radius: 24px;
    padding: 20px 0 20px 12px;
    background: linear-gradient(90deg, transparent 0%, var(--post-background) 12px);
    pointer-events: none;

    & > * {
        pointer-events: auto;
    }

    &::before {
        content: '';
        border-radius: 24px;
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            90deg,
            transparent 0%,
            var(--control-secondary-background) 12px
        );
        pointer-events: none;
    }
`;

export const ExpandableButton = styled(ChevronMultiple)
    .withConfig({
        displayName: 'ExpandableButton',
    })
    .attrs((props) => ({
        ...props,
        isHoverEnabled: false,
    }))`
    width: auto;
    min-width: 40px;
    max-width: 40px;
    margin: 0;
    overflow: hidden;

    will-change: max-width, padding;
    transform: translateZ(0);
    gap: 0px;

    transition:
        padding 0.1s ease-in,
        max-width 0.2s ease,
        gap 0.1s ease-in;

    border-radius: 20px;

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            max-width: 200px;
            gap: 8px;
        }
    }
`;

export const ExpandableButtonText = styled(Box).withConfig({
    displayName: 'ExpandableButtonText',
})`
    max-width: 0;
    opacity: 0;
    overflow: hidden;
    white-space: nowrap;

    will-change: max-width, opacity;
    transition:
        opacity 0.3s ease-out,
        max-width 0.4s ease-in-out;

    @media (hover: hover) and (pointer: fine) {
        ${ExpandableButton}:hover & {
            max-width: 200px;
            opacity: 1;
        }
    }
`;
